/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import css from '../css/AddEditPayee.module.css';
import {
  addCompany,
  AddCompanyResponse,
  addMiscPayee,
  AddMiscPayeeResponse,
  CompCodeResponse,
  editCompanyInfo,
  EditCompanyInfoResponse,
  editMiscPayeeInfo,
  EditMiscPayeeInfoResponse,
  editTaxAssessorInfo,
  EditTaxAssessorInfoResponse,
  fetchCompCodes,
  fetchMiscPayees,
  fetchQIDByKeyword,
  fetchQIDList,
  fetchTaxAssessors,
  MiscPayeeResponse,
  MiscPayeeResponseElement,
  QIDbyKeywordResponse,
  QIDListResponse,
  TaxAssessorsResponse,
} from '../common/queryFunctions';
import {
  QueryFunctionContext,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import * as constants from '../common/constants';
import { PatternFormat } from 'react-number-format';
import { isStrEmpty } from '../common/UtilityFunctions';
import DefaultModal from './modals/DefaultModal';
import { useViewingContext } from '../contexts/ViewingContext';

interface AddMiscPayeeRequestData {
  mainSendData: { [key: string]: boolean | string };
}
interface EditMiscPayeeRequestData {
  payeeNumber: string;
  mainSendData: { [key: string]: boolean | string };
}
interface EditTaxAssessorRequestData {
  assessorCode: string;
  mainSendData: { [key: string]: boolean | string };
}
interface AddCompanyRequestData {
  mainSendData: { [key: string]: boolean | string };
  addrSendData: { [key: string]: boolean | string };
}
interface EditCompanyRequestData {
  companyCode: string;
  addressType: string;
  mainSendData: { [key: string]: boolean | string };
  addrSendData: { [key: string]: boolean | string };
}
interface FormInput {
  editPayee: string; //denotes payee that is being edited, blank/ignored for add operations
  companyNameOne: string; //160 char
  companyNameTwo: string; //160 char
  addressLineOne: string; //128 char
  addressLineTwo: string; //128 char
  city: string; //30 char
  state: string; //2 char GEO5_CD.006
  zip: string; //9 char
  email: string; //71 char
  phone: string; //10 char
  phoneExt: string; //7 char
  fax: string; //10 char
  checkName: string; //70 char
  checkSupName: string; //70 char
  payeeTin: string; //9 char
}
const AddEditPayee: React.FC = () => {
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();
  const queryClient = useQueryClient();

  //state variables
  const [payeeType, setPayeeType] = useState<string>('');
  const [opType, setOpType] = useState<string>('');
  const [taxType, setTaxType] = useState<string>('');
  const [stateCode, setStateCode] = useState<string>('');
  const [stateQids, setStateQids] = useState<Array<QIDListResponse>>([]);
  const [taxQids, setTaxQids] = useState<Array<QIDListResponse>>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [formInput, setFormInput] = useState<FormInput>({
    editPayee: '',
    companyNameOne: '',
    companyNameTwo: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
    phoneExt: '',
    fax: '',
    checkName: '',
    checkSupName: '',
    payeeTin: '',
  });

  const {
    data: stateQidData,
    status: stateQidStatus,
    error: stateQidError,
  }: UseQueryResult<QIDListResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeStateQid'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchQIDList(signal, token, constants.stateQid),
    refetchOnWindowFocus: false,
  });

  const {
    data: impTypeQids,
    status: impTypeStatus,
    error: impTypeError,
  }: UseQueryResult<QIDListResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeImpTypeQids'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchQIDList(signal, token, constants.impTypeQid),
    refetchOnWindowFocus: false,
  });

  const {
    data: insuranceBusinessTypeQid,
    status: insBusStatus,
    error: insBusError,
  }: UseQueryResult<QIDbyKeywordResponse, Error> = useQuery({
    queryKey: ['addEditPayeeInsuranceBusinessTypeQid'],
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchQIDByKeyword(signal, token, constants.businessTypeQid, constants.insuranceBusinessTypeKw),
    refetchOnWindowFocus: false,
  });

  const {
    data: miBusinessTypeQid,
    status: miBusStatus,
    error: miBusError,
  }: UseQueryResult<QIDbyKeywordResponse, Error> = useQuery({
    queryKey: ['addEditPayeeMiBusinessTypeQid'],
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchQIDByKeyword(signal, token, constants.businessTypeQid, constants.miBusinessTypeKw),
    refetchOnWindowFocus: false,
  });

  const {
    data: hoaBusinessTypeQid,
    status: hoaBusStatus,
    error: hoaBusError,
  }: UseQueryResult<QIDbyKeywordResponse, Error> = useQuery({
    queryKey: ['addEditPayeeHoaBusinessTypeQid'],
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchQIDByKeyword(signal, token, constants.businessTypeQid, constants.hoaBusinessTypeKw),
    refetchOnWindowFocus: false,
  });

  const {
    data: insuranceCompanies,
    status: insCompStatus,
    error: insCompError,
  }: UseQueryResult<CompCodeResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeInsuranceCompanies'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchCompCodes(signal, token, [insuranceBusinessTypeQid.code]),
    refetchOnWindowFocus: false,
    enabled: insBusStatus === 'success',
  });

  const {
    data: miCompanies,
    status: miCompStatus,
    error: miCompError,
  }: UseQueryResult<CompCodeResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeMiCompanies'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchCompCodes(signal, token, [miBusinessTypeQid.code]),
    refetchOnWindowFocus: false,
    enabled: miBusStatus === 'success',
  });

  const {
    data: hoaCompanies,
    status: hoaCompStatus,
    error: hoaCompError,
  }: UseQueryResult<CompCodeResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeHoaCompanies'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchCompCodes(signal, token, [hoaBusinessTypeQid.code]),
    refetchOnWindowFocus: false,
    enabled: hoaBusStatus === 'success',
  });

  const {
    data: taxAssessors,
    status: taxAssessStatus,
    error: taxAssessError,
  }: UseQueryResult<TaxAssessorsResponse[], Error> = useQuery({
    queryKey: ['addEditPayeeTaxAssessors', taxType, stateCode],
    queryFn: ({ signal }: QueryFunctionContext) => fetchTaxAssessors(signal, token, stateCode, taxType),
    refetchOnWindowFocus: false,
    enabled: taxType.trim() !== '' && stateCode.trim() !== '',
  });

  const {
    data: miscPayees,
    status: miscPayeeStatus,
    error: miscPayeeError,
  }: UseQueryResult<MiscPayeeResponse, Error> = useQuery({
    queryKey: ['addEditPayeeMiscPayees'],
    queryFn: ({ signal }: QueryFunctionContext) => fetchMiscPayees(signal, token),
    refetchOnWindowFocus: false,
  });

  const StatusModal = () => {
    const mutationErrorsPresent =
      addCompanyMutation.status === 'error' ||
      editCompanyInfoMutation.status === 'error' ||
      editTaxAssessorInfoMutation.status === 'error' ||
      addMiscPayeeMutation.status === 'error' ||
      editMiscPayeeInfoMutation.status === 'error';
    const mutationSuccessPresent =
      addCompanyMutation.status === 'success' ||
      editCompanyInfoMutation.status === 'success' ||
      editTaxAssessorInfoMutation.status === 'success' ||
      addMiscPayeeMutation.status === 'success' ||
      editMiscPayeeInfoMutation.status === 'success';
    const mutationPending =
      addCompanyMutation.status === 'pending' ||
      editCompanyInfoMutation.status === 'pending' ||
      editTaxAssessorInfoMutation.status === 'pending' ||
      addMiscPayeeMutation.status === 'pending' ||
      editMiscPayeeInfoMutation.status === 'pending';

    let titleStr = '';
    if (mutationErrorsPresent) titleStr = 'Error';
    else if (mutationSuccessPresent) titleStr = 'Success';
    else if (mutationPending) titleStr = 'Processing...';

    return (
      <DefaultModal
        open={mutationErrorsPresent || mutationSuccessPresent || mutationPending}
        title={<div className={css.modalTitle}>{titleStr}</div>}
        content={
          <div className={css.modalContent}>
            {addCompanyMutation.status === 'error' && <div>Failed to submit! {addCompanyMutation.error.message}</div>}
            {editCompanyInfoMutation.status === 'error' && (
              <div>Failed to submit! {editCompanyInfoMutation.error.message}</div>
            )}
            {editTaxAssessorInfoMutation.status === 'error' && (
              <div>Failed to submit! {editTaxAssessorInfoMutation.error.message}</div>
            )}
            {addMiscPayeeMutation.status === 'error' && (
              <div>Failed to submit! {addMiscPayeeMutation.error.message}</div>
            )}
            {editMiscPayeeInfoMutation.status === 'error' && (
              <div>Failed to submit! {editMiscPayeeInfoMutation.error.message}</div>
            )}
            {mutationSuccessPresent && <div>Submission success!</div>}
          </div>
        }
        footer={
          (mutationErrorsPresent || mutationSuccessPresent) && (
            <button className={css.modalButton} onClick={reset}>
              {'OK'}
            </button>
          )
        }
      ></DefaultModal>
    );
  };

  useEffect(() => {
    if (stateQidStatus === 'pending' || stateQidStatus === 'error' || !stateQidData) {
      setStateQids([]);
    } else {
      setStateQids(stateQidData);
    }
  }, [stateQidData, stateQidStatus]);

  useEffect(() => {
    if (impTypeStatus === 'pending' || impTypeStatus === 'error' || !impTypeQids) {
      setTaxQids([]);
    } else {
      const tempTaxQids: Array<QIDListResponse> = [];
      const tempArray: Array<QIDListResponse> = impTypeQids.filter((qid) => qid.code.startsWith('TX'));
      for (let i = 0; i < tempArray.length; i++) {
        tempTaxQids.push(tempArray[i]);
      }
      setTaxQids(tempTaxQids);
    }
  }, [impTypeQids, impTypeStatus]);

  const addMiscPayeeMutation: UseMutationResult<AddMiscPayeeResponse, Error, AddMiscPayeeRequestData, unknown> =
    useMutation({
      mutationFn: (requestData: AddMiscPayeeRequestData) => {
        const controller: AbortController = new AbortController();
        abortControllerRef.current = controller;
        const signal: AbortSignal = controller.signal;
        const mutationPromise: Promise<AddMiscPayeeResponse> = addMiscPayee(signal, token, requestData.mainSendData);
        (mutationPromise as any).cancel = () => controller.abort();
        return mutationPromise;
      },
    });

  const editMiscPayeeInfoMutation: UseMutationResult<
    EditMiscPayeeInfoResponse,
    Error,
    EditMiscPayeeRequestData,
    unknown
  > = useMutation({
    mutationFn: (requestData: EditMiscPayeeRequestData) => {
      const controller: AbortController = new AbortController();
      abortControllerRef.current = controller;
      const signal: AbortSignal = controller.signal;
      const mutationPromise: Promise<EditMiscPayeeInfoResponse> = editMiscPayeeInfo(
        signal,
        token,
        requestData.payeeNumber,
        requestData.mainSendData,
      );
      (mutationPromise as any).cancel = () => controller.abort();
      return mutationPromise;
    },
  });

  const editTaxAssessorInfoMutation: UseMutationResult<
    EditTaxAssessorInfoResponse,
    Error,
    EditTaxAssessorRequestData,
    unknown
  > = useMutation({
    mutationFn: (requestData: EditTaxAssessorRequestData) => {
      const controller: AbortController = new AbortController();
      abortControllerRef.current = controller;
      const signal: AbortSignal = controller.signal;
      const mutationPromise: Promise<EditTaxAssessorInfoResponse> = editTaxAssessorInfo(
        signal,
        token,
        requestData.assessorCode,
        requestData.mainSendData,
      );
      (mutationPromise as any).cancel = () => controller.abort();
      return mutationPromise;
    },
  });

  const addCompanyMutation: UseMutationResult<AddCompanyResponse, Error, AddCompanyRequestData, unknown> = useMutation({
    mutationFn: (requestData: AddCompanyRequestData) => {
      const controller: AbortController = new AbortController();
      abortControllerRef.current = controller;
      const signal: AbortSignal = controller.signal;
      const mutationPromise: Promise<AddCompanyResponse> = addCompany(
        signal,
        token,
        requestData.mainSendData,
        requestData.addrSendData,
      );
      (mutationPromise as any).cancel = () => controller.abort();
      return mutationPromise;
    },
  });

  const editCompanyInfoMutation: UseMutationResult<EditCompanyInfoResponse, Error, EditCompanyRequestData, unknown> =
    useMutation({
      mutationFn: (requestData: EditCompanyRequestData) => {
        const controller: AbortController = new AbortController();
        abortControllerRef.current = controller;
        const signal: AbortSignal = controller.signal;
        const mutationPromise: Promise<EditCompanyInfoResponse> = editCompanyInfo(
          signal,
          token,
          requestData.companyCode,
          requestData.addressType,
          requestData.mainSendData,
          requestData.addrSendData,
        );
        (mutationPromise as any).cancel = () => controller.abort();
        return mutationPromise;
      },
    });

  const clearFormInput = useCallback(() => {
    setFormInput({
      editPayee: '',
      companyNameOne: '',
      companyNameTwo: '',
      addressLineOne: '',
      addressLineTwo: '',
      city: '',
      state: '',
      zip: '',
      email: '',
      phone: '',
      phoneExt: '',
      fax: '',
      checkName: '',
      checkSupName: '',
      payeeTin: '',
    });
  }, []);

  const clearInputs = useCallback(() => {
    setPayeeType('');
    setOpType('');
    setTaxType('');
    setStateCode('');
    clearFormInput();
  }, [clearFormInput]);

  const resetMutations = useCallback(() => {
    //reset mutations' status
    addCompanyMutation.reset();
    editCompanyInfoMutation.reset();
    editTaxAssessorInfoMutation.reset();
    addMiscPayeeMutation.reset();
    editMiscPayeeInfoMutation.reset();
  }, [
    addCompanyMutation,
    editCompanyInfoMutation,
    editTaxAssessorInfoMutation,
    addMiscPayeeMutation,
    editMiscPayeeInfoMutation,
  ]);

  const handleRefetch = useCallback(async () => {
    if (
      (addCompanyMutation.status === 'success' || editCompanyInfoMutation.status === 'success') &&
      payeeType === 'hazIns'
    ) {
      await queryClient.resetQueries({ queryKey: ['addEditPayeeInsuranceCompanies'] });
    }
    if (
      (addCompanyMutation.status === 'success' || editCompanyInfoMutation.status === 'success') &&
      payeeType === 'mortIns'
    ) {
      await queryClient.resetQueries({ queryKey: ['addEditPayeeMiCompanies'] });
    }
    if (
      (addCompanyMutation.status === 'success' || editCompanyInfoMutation.status === 'success') &&
      payeeType === 'hoaComp'
    ) {
      await queryClient.resetQueries({ queryKey: ['addEditPayeeHoaCompanies'] });
    }
    if (
      (addMiscPayeeMutation.status === 'success' || editMiscPayeeInfoMutation.status === 'success') &&
      payeeType === 'miscPay'
    ) {
      await queryClient.resetQueries({ queryKey: ['addEditPayeeMiscPayees'] });
    }
    if (editTaxAssessorInfoMutation.status === 'success' && payeeType === 'taxAss') {
      await queryClient.resetQueries({ queryKey: ['addEditPayeeTaxAssessors', taxType, stateCode] });
    }
  }, [
    payeeType,
    queryClient,
    stateCode,
    taxType,
    addCompanyMutation.status,
    editCompanyInfoMutation.status,
    addMiscPayeeMutation.status,
    editMiscPayeeInfoMutation.status,
    editTaxAssessorInfoMutation.status,
  ]);

  const reset = useCallback(() => {
    handleRefetch();
    clearInputs();
    resetMutations();
  }, [clearInputs, resetMutations, handleRefetch]);

  const handlePayeeTypeChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value: string = event.target.value;
      setPayeeType(value);
      setOpType('');
      setTaxType('');
      setStateCode('');
      //reset mutations' status
      addCompanyMutation.reset();
      editCompanyInfoMutation.reset();
      editTaxAssessorInfoMutation.reset();
      addMiscPayeeMutation.reset();
      editMiscPayeeInfoMutation.reset();
      setFormInput({
        editPayee: '',
        companyNameOne: '',
        companyNameTwo: '',
        addressLineOne: '',
        addressLineTwo: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        phoneExt: '',
        fax: '',
        checkName: '',
        checkSupName: '',
        payeeTin: '',
      });
    },
    [
      addCompanyMutation,
      editCompanyInfoMutation,
      editTaxAssessorInfoMutation,
      addMiscPayeeMutation,
      editMiscPayeeInfoMutation,
    ],
  );

  const handleOpTypeChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value: string = event.target.value;
      setOpType(value);
      setTaxType('');
      setStateCode('');
      //reset mutations' status
      addCompanyMutation.reset();
      editCompanyInfoMutation.reset();
      editTaxAssessorInfoMutation.reset();
      addMiscPayeeMutation.reset();
      editMiscPayeeInfoMutation.reset();
      setFormInput({
        editPayee: '',
        companyNameOne: '',
        companyNameTwo: '',
        addressLineOne: '',
        addressLineTwo: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        phoneExt: '',
        fax: '',
        checkName: '',
        checkSupName: '',
        payeeTin: '',
      });
    },
    [
      addCompanyMutation,
      editCompanyInfoMutation,
      editTaxAssessorInfoMutation,
      addMiscPayeeMutation,
      editMiscPayeeInfoMutation,
    ],
  );

  const handleTaxTypeChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value: string = event.target.value;
      setTaxType(value);
      setStateCode('');
      //reset mutations' status
      addCompanyMutation.reset();
      editCompanyInfoMutation.reset();
      editTaxAssessorInfoMutation.reset();
      addMiscPayeeMutation.reset();
      editMiscPayeeInfoMutation.reset();
      setFormInput({
        editPayee: '',
        companyNameOne: '',
        companyNameTwo: '',
        addressLineOne: '',
        addressLineTwo: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        phoneExt: '',
        fax: '',
        checkName: '',
        checkSupName: '',
        payeeTin: '',
      });
    },
    [
      addCompanyMutation,
      editCompanyInfoMutation,
      editTaxAssessorInfoMutation,
      addMiscPayeeMutation,
      editMiscPayeeInfoMutation,
    ],
  );

  const handleStateCodeChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value: string = event.target.value;
      setStateCode(value);
      //reset mutations' status
      addCompanyMutation.reset();
      editCompanyInfoMutation.reset();
      editTaxAssessorInfoMutation.reset();
      addMiscPayeeMutation.reset();
      editMiscPayeeInfoMutation.reset();
      setFormInput({
        editPayee: '',
        companyNameOne: '',
        companyNameTwo: '',
        addressLineOne: '',
        addressLineTwo: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        phoneExt: '',
        fax: '',
        checkName: '',
        checkSupName: '',
        payeeTin: '',
      });
    },
    [
      addCompanyMutation,
      editCompanyInfoMutation,
      editTaxAssessorInfoMutation,
      addMiscPayeeMutation,
      editMiscPayeeInfoMutation,
    ],
  );

  const updateFormInput = useCallback((event: { target: { name: string; value: string; type: string } }) => {
    const name: string = event.target.name;
    const value: string = event.target.value;

    setFormInput((prevFormInput) => {
      const newFormInput: FormInput = { ...prevFormInput };
      newFormInput[name] = value;
      return newFormInput;
    });
  }, []);

  const updateCurrentFormInput = useCallback(
    async (payee: CompCodeResponse | TaxAssessorsResponse | MiscPayeeResponseElement) => {
      if (!payee) {
        setFormInput({
          editPayee:
            'companyCode' in payee
              ? `${payee.companyCode} - ${payee.name}`
              : 'assessorCode' in payee
                ? `${payee.assessorCode} - ${payee.nameOne}`
                : `${payee.payeeNumber} - ${payee.nameOne}`,
          companyNameOne: '',
          companyNameTwo: '',
          addressLineOne: '',
          addressLineTwo: '',
          city: '',
          state: '',
          zip: '',
          email: '',
          phone: '',
          phoneExt: '',
          fax: '',
          checkName: '',
          checkSupName: '',
          payeeTin: '',
        });
      } else if (payee && 'companyCode' in payee) {
        setFormInput({
          editPayee: `${payee.companyCode} - ${payee.name}`,
          companyNameOne: payee.name,
          companyNameTwo: payee.name2,
          addressLineOne: payee.address1,
          addressLineTwo: payee.address2,
          city: payee.city,
          state: payee.state,
          zip: payee.zip,
          email: payee.contactEmail,
          phone: payee.phoneNumber,
          phoneExt: payee.phoneExt,
          fax: payee.faxNumber,
          checkName: '',
          checkSupName: '',
          payeeTin: '',
        });
      } else if (payee && 'assessorCode' in payee) {
        setFormInput({
          editPayee: `${payee.assessorCode} - ${payee.nameOne}`,
          companyNameOne: '',
          companyNameTwo: '',
          addressLineOne: '',
          addressLineTwo: '',
          city: '',
          state: '',
          zip: '',
          email: '',
          phone: '',
          phoneExt: '',
          fax: '',
          checkName: payee.checkName,
          checkSupName: payee.suppCheckName,
          payeeTin: '',
        });
      } else if (payee && 'payeeNumber' in payee) {
        setFormInput({
          editPayee: `${payee.payeeNumber} - ${payee.nameOne}`,
          companyNameOne: payee.nameOne,
          companyNameTwo: payee.nameTwo,
          addressLineOne: payee.addrOne,
          addressLineTwo: payee.addrTwo,
          city: payee.city,
          state: payee.state,
          zip: payee.zip,
          email: payee.emailAddr,
          phone: payee.phoneNo,
          phoneExt: '',
          fax: '',
          checkName: '',
          checkSupName: '',
          payeeTin: payee.payeeTin,
        });
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      //reset mutations' status
      addCompanyMutation.reset();
      editCompanyInfoMutation.reset();
      editTaxAssessorInfoMutation.reset();
      addMiscPayeeMutation.reset();
      editMiscPayeeInfoMutation.reset();

      //validation checks
      const payeeCode = formInput.editPayee.split(' ')[0];
      if (opType === 'edit') {
        if (payeeType === 'hazIns') {
          const matchedPayee: CompCodeResponse = insuranceCompanies.find(
            (insuranceComp) => insuranceComp.companyCode === payeeCode,
          );
          if (!matchedPayee) {
            alert('Invalid Hazard Insurance payee selected.');
            return;
          }
        }
        if (payeeType === 'mortIns') {
          const matchedPayee: CompCodeResponse = miCompanies.find((miComp) => miComp.companyCode === payeeCode);
          if (!matchedPayee) {
            alert('Invalid Mortgage Insurance payee selected.');
            return;
          }
        }
        if (payeeType === 'hoaComp') {
          const matchedPayee: CompCodeResponse = hoaCompanies.find((hoaComp) => hoaComp.companyCode === payeeCode);
          if (!matchedPayee) {
            alert('Invalid HOA payee selected.');
            return;
          }
        }
        if (payeeType === 'taxAss') {
          const matchedPayee: TaxAssessorsResponse = taxAssessors.find(
            (taxAssessor) => taxAssessor.assessorCode === payeeCode,
          );
          if (!matchedPayee) {
            alert('Invalid Tax Assessor payee selected.');
            return;
          }
        }
        if (payeeType === 'miscPay') {
          const matchedPayee: MiscPayeeResponseElement = miscPayees.payees.find(
            (miscPayee) => miscPayee.payeeNumber === payeeCode,
          );
          if (!matchedPayee) {
            alert('Invalid Miscellaneous payee selected.');
            return;
          }
        }
      }
      //check for blank input on required fields
      if (payeeType !== 'taxAss' && formInput.companyNameOne.trim() === '') {
        alert('Company Name One cannot be blank.');
        return;
      }
      if (payeeType !== 'taxAss' && formInput.addressLineOne.trim() === '') {
        alert('Street Address Line One cannot be blank.');
        return;
      }
      if (payeeType !== 'taxAss' && formInput.city.trim() === '') {
        alert('City cannot be blank.');
        return;
      }
      //state and zip do not need blank input validation as those fields prevent blank values innately

      //zip code must be five or nine digits
      if (payeeType !== 'taxAss' && !constants.zipCodePattern.test(formInput.zip)) {
        alert('Invalid Zip Code provided.');
        return;
      }
      //email validation
      if (payeeType !== 'taxAss' && !isStrEmpty(formInput.email) && !constants.emailPattern.test(formInput.email)) {
        alert('Invalid Email provided.');
      }
      //phone and fax numbers must be ten digits
      if (payeeType !== 'taxAss' && !isStrEmpty(formInput.phone) && !constants.phoneNoPattern.test(formInput.phone)) {
        alert('Invalid Phone Number provided.');
        return;
      }
      if (
        (payeeType === 'hazIns' || payeeType === 'mortIns' || payeeType === 'hoaComp') &&
        !isStrEmpty(formInput.fax) &&
        !constants.phoneNoPattern.test(formInput.fax)
      ) {
        alert('Invalid Fax Number provided.');
        return;
      }
      //tax identification number
      if (
        payeeType === 'miscPay' &&
        !isStrEmpty(formInput.payeeTin) &&
        !constants.payeeTinPattern.test(formInput.payeeTin)
      ) {
        alert('Invalid Tax Identification Number provided.');
        return;
      }
      const mainSendData: { [key: string]: boolean | string } = {}; //main data is dynamically constructed
      const addrSendData: { [key: string]: boolean | string } = {}; //utilized for CMPB_CD primary address data
      mainSendData.active = true;
      addrSendData.active = true;
      if (payeeType === 'hazIns' || payeeType === 'mortIns' || payeeType === 'hoaComp') {
        const companyCode: string = payeeCode;
        const addressType: string = constants.primaryAddress;
        const priBusType: string =
          payeeType === 'hazIns' && insuranceBusinessTypeQid
            ? insuranceBusinessTypeQid.code
            : payeeType === 'mortIns' && miBusinessTypeQid
              ? miBusinessTypeQid.code
              : payeeType === 'hoaComp' && hoaBusinessTypeQid
                ? hoaBusinessTypeQid.code
                : '';
        if (!isStrEmpty(priBusType)) {
          mainSendData.primaryBusinessType = priBusType;
        } else {
          alert(
            'Failed to send request due to issue with obtaining primary business type. Refresh page and try again. If issue persists contact Red Carpet Servicing Development.',
          );
          return;
        }
        mainSendData.name1 = formInput.companyNameOne.trim(); //required
        mainSendData.name2 = formInput.companyNameTwo ? formInput.companyNameTwo.trim() : '';
        mainSendData.stateOfInc = formInput.state.trim(); //required
        mainSendData.primaryContactEmail = formInput.email ? formInput.email.trim() : '';
        mainSendData.primaryContactPhone = formInput.phone ? formInput.phone.trim() : '';
        mainSendData.primaryContactPhoneExtension = formInput.phoneExt ? formInput.phoneExt.trim() : '';
        mainSendData.primaryContactFaxNumber = formInput.fax ? formInput.fax.trim() : '';
        addrSendData.address1 = formInput.addressLineOne.trim(); //required
        addrSendData.address2 = formInput.addressLineTwo ? formInput.addressLineTwo.trim() : '';
        addrSendData.city = formInput.city.trim(); //required
        addrSendData.state = formInput.state.trim(); //required
        addrSendData.zipCode = formInput.zip.trim(); //required
        addrSendData.email = formInput.email ? formInput.email.trim() : '';
        addrSendData.phoneNumber = formInput.phone ? formInput.phone.trim() : '';
        addrSendData.phoneExtension = formInput.phoneExt ? formInput.phoneExt.trim() : '';
        addrSendData.faxNumber = formInput.fax ? formInput.fax.trim() : '';
        if (opType === 'add') {
          addCompanyMutation.mutate({ mainSendData, addrSendData });
          return;
        }
        if (opType === 'edit') {
          editCompanyInfoMutation.mutate({ companyCode, addressType, mainSendData, addrSendData });
          return;
        }
      }
      if (payeeType === 'taxAss' && opType === 'edit') {
        const assessorCode: string = payeeCode;
        mainSendData.checkName = formInput.checkName.trim();
        mainSendData.suppCheckName = formInput.checkSupName.trim();
        editTaxAssessorInfoMutation.mutate({ assessorCode, mainSendData });
        return;
      }
      if (payeeType === 'miscPay') {
        const payeeNumber: string = payeeCode;
        mainSendData.nameOne = formInput.companyNameOne.trim(); //required
        mainSendData.nameTwo = formInput.companyNameTwo ? formInput.companyNameTwo.trim() : '';
        mainSendData.addrOne = formInput.addressLineOne.trim(); //required
        mainSendData.addrTwo = formInput.addressLineTwo ? formInput.addressLineTwo.trim() : '';
        mainSendData.city = formInput.city.trim(); //required
        mainSendData.state = formInput.state.trim(); //required
        mainSendData.zip = formInput.zip.trim(); //required
        mainSendData.emailAddr = formInput.email ? formInput.email.trim() : '';
        mainSendData.phoneNo = formInput.phone ? formInput.phone.trim() : '';
        mainSendData.payeeTin = formInput.payeeTin ? formInput.payeeTin.trim() : '';
        if (opType === 'add') {
          addMiscPayeeMutation.mutate({ mainSendData });
          return;
        }
        if (opType === 'edit') {
          editMiscPayeeInfoMutation.mutate({ payeeNumber, mainSendData });
          return;
        }
      }
    },
    [
      formInput.editPayee,
      formInput.zip,
      formInput.email,
      formInput.phone,
      formInput.fax,
      formInput.addressLineOne,
      formInput.addressLineTwo,
      formInput.city,
      formInput.companyNameOne,
      formInput.companyNameTwo,
      formInput.phoneExt,
      formInput.state,
      insuranceCompanies,
      miCompanies,
      hoaCompanies,
      insuranceBusinessTypeQid,
      miBusinessTypeQid,
      hoaBusinessTypeQid,
      miscPayees,
      formInput.payeeTin,
      opType,
      payeeType,
      taxAssessors,
      formInput.checkName,
      formInput.checkSupName,
      addCompanyMutation,
      editCompanyInfoMutation,
      editTaxAssessorInfoMutation,
      addMiscPayeeMutation,
      editMiscPayeeInfoMutation,
    ],
  );

  const [filteredEditPayees, setFilteredEditPayees] = useState<
    Array<CompCodeResponse> | Array<TaxAssessorsResponse> | MiscPayeeResponseElement[]
  >([]);
  useEffect(() => {
    if (
      (payeeType === 'hazIns' || payeeType === 'mortIns' || payeeType === 'hoaComp' || payeeType === 'miscPay') &&
      (insCompStatus === 'pending' ||
        insCompStatus === 'error' ||
        miCompStatus === 'pending' ||
        miCompStatus === 'error' ||
        hoaCompStatus === 'pending' ||
        hoaCompStatus === 'error' ||
        miscPayeeStatus === 'pending' ||
        miscPayeeStatus === 'error' ||
        !insuranceCompanies ||
        !miCompanies ||
        !hoaCompanies ||
        !miscPayees)
    ) {
      setFilteredEditPayees([]);
    } else if (
      payeeType === 'taxAss' &&
      (taxAssessStatus === 'pending' || taxAssessStatus === 'error' || !taxAssessors)
    ) {
      setFilteredEditPayees([]);
    } else {
      const tempFilteredInsuranceComps: Array<CompCodeResponse> = [];
      const tempFilteredTaxAssessors: Array<TaxAssessorsResponse> = [];
      const tempFilteredMiscPayees: Array<MiscPayeeResponseElement> = [];
      if (formInput.editPayee && payeeType === 'hazIns') {
        const tempArray: Array<CompCodeResponse> = insuranceCompanies.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase().includes(formInput.editPayee.toLowerCase()) ||
            insuranceComp.name.toLowerCase().includes(formInput.editPayee.toLowerCase()),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempFilteredInsuranceComps.push(tempArray[i]);
        }
      }
      if (formInput.editPayee && payeeType === 'mortIns') {
        const tempArray: Array<CompCodeResponse> = miCompanies.filter(
          (insuranceComp) =>
            insuranceComp.companyCode.toLowerCase().includes(formInput.editPayee.toLowerCase()) ||
            insuranceComp.name.toLowerCase().includes(formInput.editPayee.toLowerCase()),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempFilteredInsuranceComps.push(tempArray[i]);
        }
      }
      if (formInput.editPayee && payeeType === 'hoaComp') {
        const tempArray: Array<CompCodeResponse> = hoaCompanies.filter(
          (hoaComp) =>
            hoaComp.companyCode.toLowerCase().includes(formInput.editPayee.toLowerCase()) ||
            hoaComp.name.toLowerCase().includes(formInput.editPayee.toLowerCase()),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempFilteredInsuranceComps.push(tempArray[i]);
        }
      }
      if (formInput.editPayee && payeeType === 'taxAss') {
        console.log(typeof taxAssessors);
        const tempArray: Array<TaxAssessorsResponse> = taxAssessors.filter(
          (taxAssessor) =>
            taxAssessor.assessorCode.toLowerCase().includes(formInput.editPayee.toLowerCase()) ||
            taxAssessor.nameOne.toLowerCase().includes(formInput.editPayee.toLowerCase()),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempFilteredTaxAssessors.push(tempArray[i]);
        }
      }
      if (formInput.editPayee && payeeType === 'miscPay') {
        console.log(Array.isArray(miscPayees));
        console.log(typeof miscPayees);
        const tempArray: Array<MiscPayeeResponseElement> = miscPayees.payees.filter(
          (miscPayee) =>
            miscPayee.payeeNumber.toLowerCase().includes(formInput.editPayee.toLowerCase()) ||
            miscPayee.nameOne.toLowerCase().includes(formInput.editPayee.toLowerCase()),
        );
        for (let i = 0; i < tempArray.length; i++) {
          tempFilteredMiscPayees.push(tempArray[i]);
        }
      }
      if (payeeType === 'hazIns' || payeeType === 'mortIns' || payeeType === 'hoaComp') {
        setFilteredEditPayees(tempFilteredInsuranceComps);
      }
      if (payeeType === 'taxAss') {
        setFilteredEditPayees(tempFilteredTaxAssessors);
      }
      if (payeeType === 'miscPay') {
        setFilteredEditPayees(tempFilteredMiscPayees);
      }
    }
  }, [
    formInput.editPayee,
    insCompStatus,
    miCompStatus,
    hoaCompStatus,
    taxAssessStatus,
    insuranceCompanies,
    miCompanies,
    hoaCompanies,
    taxAssessors,
    payeeType,
    miscPayeeStatus,
    miscPayees,
  ]);

  //type guard functions for edit payee checking
  const isCompCodeResponseArray = useCallback(
    (arr: (CompCodeResponse | TaxAssessorsResponse | MiscPayeeResponseElement)[]): arr is CompCodeResponse[] => {
      return arr.length > 0 && 'companyCode' in arr[0];
    },
    [],
  );

  const isTaxAssessorsResponseArray = useCallback(
    (arr: (CompCodeResponse | TaxAssessorsResponse | MiscPayeeResponseElement)[]): arr is TaxAssessorsResponse[] => {
      return arr.length > 0 && 'assessorCode' in arr[0];
    },
    [],
  );

  const isMiscPayeeResponseArray = useCallback(
    (
      arr: (CompCodeResponse | TaxAssessorsResponse | MiscPayeeResponseElement)[],
    ): arr is MiscPayeeResponseElement[] => {
      return arr.length > 0 && 'payeeNumber' in arr[0];
    },
    [],
  );

  if (!isServicerView) return <div className='access-error-msg'>{constants.borrowerNoAccessMsg}</div>;

  if (
    stateQidStatus === 'pending' ||
    impTypeStatus === 'pending' ||
    insBusStatus === 'pending' ||
    miBusStatus === 'pending' ||
    hoaBusStatus === 'pending' ||
    insCompStatus === 'pending' ||
    miCompStatus === 'pending' ||
    hoaCompStatus === 'pending' ||
    miscPayeeStatus === 'pending'
  )
    return <div>Loading...</div>;
  if (
    stateQidStatus === 'error' ||
    impTypeStatus === 'error' ||
    insBusStatus === 'error' ||
    miBusStatus === 'error' ||
    hoaBusStatus === 'error' ||
    insCompStatus === 'error' ||
    miCompStatus === 'error' ||
    hoaCompStatus === 'error' ||
    miscPayeeStatus === 'error'
  )
    return (
      <>
        <h2>Encountered errors while setting up Add/Edit Payee Form</h2>
        <ul>
          {stateQidStatus === 'error' && <li>{stateQidError.message}</li>}
          {impTypeStatus === 'error' && <li>{impTypeError.message}</li>}
          {insBusStatus === 'error' && <li>{insBusError.message}</li>}
          {miBusStatus === 'error' && <li>{miBusError.message}</li>}
          {hoaBusStatus === 'error' && <li>{hoaBusError.message}</li>}
          {insCompStatus === 'error' && <li>{insCompError.message}</li>}
          {miCompStatus === 'error' && <li>{miCompError.message}</li>}
          {hoaCompStatus === 'error' && <li>{hoaCompError.message}</li>}
          {miscPayeeStatus === 'error' && <li>{miscPayeeError.message}</li>}
        </ul>
      </>
    );
  return (
    <>
      <div>
        <span>
          <span className={css.required}>*</span> = required field
        </span>
      </div>
      <div className={css.payeeTypeFieldSet}>
        <span>
          Select Payee Type<span className={css.required}>*</span>
        </span>
        <label>
          <input
            type='radio'
            name='payeeType'
            value='hazIns'
            checked={payeeType === 'hazIns'}
            onChange={handlePayeeTypeChange}
          ></input>
          Hazard Ins.
        </label>
        <label>
          <input
            type='radio'
            name='payeeType'
            value='mortIns'
            checked={payeeType === 'mortIns'}
            onChange={handlePayeeTypeChange}
          ></input>
          Mortgage Ins.
        </label>
        <label>
          <input
            type='radio'
            name='payeeType'
            value='hoaComp'
            checked={payeeType === 'hoaComp'}
            onChange={handlePayeeTypeChange}
          ></input>
          HOA
        </label>
        <label>
          <input
            type='radio'
            name='payeeType'
            value='taxAss'
            checked={payeeType === 'taxAss'}
            onChange={handlePayeeTypeChange}
          ></input>
          Tax Assessor
        </label>
        <label>
          <input
            type='radio'
            name='payeeType'
            value='miscPay'
            checked={payeeType === 'miscPay'}
            onChange={handlePayeeTypeChange}
          ></input>
          Misc. Payee
        </label>
      </div>
      {payeeType && (
        <div className={css.operationTypeFieldSet}>
          <span>
            Select Operation Type<span className={css.required}>*</span>
          </span>
          {payeeType !== 'taxAss' && (
            <label>
              <input
                type='radio'
                name='opType'
                value='add'
                checked={opType === 'add'}
                onChange={handleOpTypeChange}
              ></input>
              Add
            </label>
          )}
          <label>
            <input
              type='radio'
              name='opType'
              value='edit'
              checked={opType === 'edit'}
              onChange={handleOpTypeChange}
            ></input>
            Edit
          </label>
        </div>
      )}
      {payeeType && opType && (
        <form className={css.addEditPayeeForm} onSubmit={handleSubmit}>
          {payeeType === 'taxAss' && opType === 'edit' && (
            <label>
              <span>
                Select Tax Type<span className={css.required}>*</span>
              </span>
              <select name='taxType' value={taxType} onChange={handleTaxTypeChange} required>
                <option value=''>Select Tax Type</option>
                {taxQids &&
                  taxQids.length &&
                  taxQids.map((qid, index) => (
                    <option key={index} value={qid.code}>{`${qid.code} - ${qid.description}`}</option>
                  ))}
              </select>
            </label>
          )}
          {payeeType === 'taxAss' && opType === 'edit' && taxType && (
            <label>
              <span>
                Select State Code<span className={css.required}>*</span>
              </span>
              <select name='stateCode' value={stateCode} onChange={handleStateCodeChange} required>
                <option value=''>Select State Code</option>
                {stateQids &&
                  stateQids.length &&
                  stateQids.map((qid, index) => (
                    <option key={index} value={qid.code}>{`${qid.code} - ${qid.description}`}</option>
                  ))}
              </select>
            </label>
          )}
          {payeeType === 'taxAss' && opType === 'edit' && taxType && stateCode && taxAssessStatus === 'pending' && (
            <div>Loading Tax Assessor Payees...</div>
          )}
          {payeeType === 'taxAss' && opType === 'edit' && taxType && stateCode && taxAssessStatus === 'error' && (
            <div>{taxAssessError.message}</div>
          )}
          {opType === 'edit' && (payeeType !== 'taxAss' || (taxType && stateCode && taxAssessStatus === 'success')) && (
            <label>
              <span>
                Select Payee<span className={css.required}>*</span>
              </span>
              <input
                type='text'
                name='editPayee'
                value={formInput.editPayee}
                onChange={updateFormInput}
                placeholder='Enter Insurance Company Code/Name'
                required
                autoComplete='off'
              ></input>
              {filteredEditPayees.length !== 0 && (
                <div className={css.dropdown}>
                  <ul>
                    {filteredEditPayees &&
                      isCompCodeResponseArray(filteredEditPayees) &&
                      filteredEditPayees.map((payee) => (
                        <li>
                          <button
                            type='button'
                            key={`comp-${payee.companyCode}`}
                            onClick={() => updateCurrentFormInput(payee)}
                          >{`${payee.companyCode} - ${payee.name}`}</button>
                        </li>
                      ))}
                    {filteredEditPayees &&
                      isTaxAssessorsResponseArray(filteredEditPayees) &&
                      filteredEditPayees.map((payee) => (
                        <li>
                          <button
                            type='button'
                            key={`tax-${payee.assessorCode}`}
                            onClick={() => updateCurrentFormInput(payee)}
                          >{`${payee.assessorCode} - ${payee.nameOne}`}</button>
                        </li>
                      ))}
                    {filteredEditPayees &&
                      isMiscPayeeResponseArray(filteredEditPayees) &&
                      filteredEditPayees.map((payee) => (
                        <li>
                          <button
                            type='button'
                            key={`misc-${payee.payeeNumber}`}
                            onClick={() => updateCurrentFormInput(payee)}
                          >{`${payee.payeeNumber} - ${payee.nameOne}`}</button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </label>
          )}
          {payeeType !== 'taxAss' && (opType === 'add' || (opType === 'edit' && formInput.editPayee)) && (
            <>
              <label>
                <span>
                  Company Name One<span className={css.required}>*</span>
                </span>
                <input
                  type='text'
                  name='companyNameOne'
                  value={formInput.companyNameOne}
                  maxLength={payeeType === 'miscPay' ? 70 : 160}
                  onChange={updateFormInput}
                  required
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>Company Name Two</span>
                <input
                  type='text'
                  name='companyNameTwo'
                  value={formInput.companyNameTwo}
                  maxLength={payeeType === 'miscPay' ? 70 : 160}
                  onChange={updateFormInput}
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>
                  Street Address Line One<span className={css.required}>*</span>
                </span>
                <input
                  type='text'
                  name='addressLineOne'
                  value={formInput.addressLineOne}
                  maxLength={payeeType === 'miscPay' ? 50 : 128}
                  onChange={updateFormInput}
                  required
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>Street Address Line Two</span>
                <input
                  type='text'
                  name='addressLineTwo'
                  value={formInput.addressLineTwo}
                  maxLength={payeeType === 'miscPay' ? 50 : 128}
                  onChange={updateFormInput}
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>
                  City<span className={css.required}>*</span>
                </span>
                <input
                  type='text'
                  name='city'
                  value={formInput.city}
                  maxLength={30}
                  onChange={updateFormInput}
                  required
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>
                  State<span className={css.required}>*</span>
                </span>
                <select name='state' value={formInput.state} onChange={updateFormInput} required>
                  <option value=''>Select State</option>
                  {stateQids &&
                    stateQids.length &&
                    stateQids.map((qid, index) => (
                      <option key={index} value={qid.code}>{`${qid.code} - ${qid.description}`}</option>
                    ))}
                </select>
              </label>
              <label>
                <span>
                  Zip Code<span className={css.required}>*</span>
                </span>
                <PatternFormat
                  format='#####-####'
                  value={formInput.zip}
                  valueIsNumericString={true}
                  placeholder='Enter 5 or 9 digit zip code'
                  name='zip'
                  required
                  autoComplete='off'
                  onValueChange={(values) => {
                    const { value } = values;
                    updateFormInput({ target: { name: 'zip', value: value, type: 'text' } });
                  }}
                ></PatternFormat>
              </label>
              <label>
                <span>Email</span>
                <input
                  type='text'
                  name='email'
                  value={formInput.email}
                  placeholder='Enter email'
                  maxLength={71}
                  onChange={updateFormInput}
                  autoComplete='off'
                />
              </label>
              <label>
                <span>Phone No</span>
                <PatternFormat
                  format='###-###-####'
                  value={formInput.phone}
                  valueIsNumericString={true}
                  placeholder='Enter 10 digit phone number'
                  name='phone'
                  autoComplete='off'
                  onValueChange={(values) => {
                    const { value } = values;
                    updateFormInput({ target: { name: 'phone', value: value, type: 'text' } });
                  }}
                ></PatternFormat>
              </label>
              {payeeType && (payeeType === 'hazIns' || payeeType === 'mortIns' || payeeType === 'hoaComp') && (
                <>
                  <label>
                    <span>Phone Ext</span>
                    <PatternFormat
                      format='#######'
                      value={formInput.phoneExt}
                      valueIsNumericString={true}
                      placeholder='Enter phone extension'
                      name='phoneExt'
                      autoComplete='off'
                      onValueChange={(values) => {
                        const { value } = values;
                        updateFormInput({ target: { name: 'phoneExt', value: value, type: 'text' } });
                      }}
                    ></PatternFormat>
                  </label>
                  <label>
                    <span>Fax No</span>
                    <PatternFormat
                      format='###-###-####'
                      value={formInput.fax}
                      valueIsNumericString={true}
                      placeholder='Enter 10 digit fax number'
                      name='fax'
                      autoComplete='off'
                      onValueChange={(values) => {
                        const { value } = values;
                        updateFormInput({ target: { name: 'fax', value: value, type: 'text' } });
                      }}
                    ></PatternFormat>
                  </label>
                </>
              )}
              {payeeType && payeeType === 'miscPay' && (
                <label>
                  <span>Tax Identification No</span>
                  <PatternFormat
                    format='#########'
                    value={formInput.payeeTin}
                    valueIsNumericString={true}
                    placeholder='Enter 9 digit tax identification number'
                    name='payeeTin'
                    autoComplete='off'
                    onValueChange={(values) => {
                      const { value } = values;
                      updateFormInput({ target: { name: 'payeeTin', value: value, type: 'text' } });
                    }}
                  ></PatternFormat>
                </label>
              )}
            </>
          )}
          {payeeType && payeeType === 'taxAss' && opType === 'edit' && formInput.editPayee && (
            <>
              <label>
                <span>Check Name</span>
                <input
                  type='text'
                  name='checkName'
                  value={formInput.checkName}
                  maxLength={70}
                  onChange={updateFormInput}
                  autoComplete='off'
                ></input>
              </label>
              <label>
                <span>Supplemental Check Name</span>
                <input
                  type='text'
                  name='checkSupName'
                  value={formInput.checkSupName}
                  maxLength={70}
                  onChange={updateFormInput}
                  autoComplete='off'
                ></input>
              </label>
            </>
          )}
          {payeeType && (opType === 'add' || (opType === 'edit' && formInput.editPayee)) && (
            <button className={css.addEditPayeeFormSubmitButton} type='submit'>
              Submit
            </button>
          )}
        </form>
      )}
      {StatusModal()}
    </>
  );
};
export default AddEditPayee;
